/**
 * Bulgarian and English, with Bulgarian the default.
 *
 * The console owns every user-facing string. The API sends codes; this is where they
 * become words, which is what lets one backend serve both languages without a redeploy.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { baseLanguage, fallbackLanguage, resourcesFor, supportedLanguages } from '@taxi/i18n';

import bg from './locales/bg.json';
import en from './locales/en.json';

export { supportedLanguages };
export type { SupportedLanguage } from '@taxi/i18n';

await i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: resourcesFor({ bg, en }),
    // Bulgarian, because the first tenants are Bulgarian. A tenant's configured
    // culture overrides this once one is resolved.
    fallbackLng: fallbackLanguage,
    supportedLngs: supportedLanguages,
    interpolation: {
      // React escapes for us; doing it twice turns an apostrophe into &#39; on screen.
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

/** Switches language, for the tenant default or an explicit choice. */
export async function setLanguage(language: string): Promise<void> {
  // Tenant cultures are full BCP-47 (bg-BG, en-IE) while the bundles are keyed by the
  // base language, so the region is dropped rather than failing to match.
  await i18n.changeLanguage(baseLanguage(language));
}

export default i18n;
